import React, {useEffect, useState} from 'react';
import { useSelector, useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  Card,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableSortLabel,
  Checkbox,
  Drawer
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { getStoreRequestAccessListApi, getStoreCustomerGroupsApi } from 'api';
import { Breadcrumb, BackdropCircular} from 'components';
import { useStyles } from '../styles';
import FilterDrawer from './FilterDrawer';
import RequestAccessRow from './RequestAccessRow';
import RequestAccessTableToolbar from './RequestAccessTableToolbar';


const defaultCustomerGroups = [
  {
    code: 'Not Logged In',
    id: 0
  },
  {
    code: 'General',
    id: 1
  }
];


const RequestAccessList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortField, setSortField] = useState('entity_id');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchConditions, setSearchConditions] = useState({});
  const [checkedCustomers, setCheckedCustomers] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterConditions, setFilterConditions] = useState({});

  const {
    requestAccessCustomersList,
    customersTotalCount,
    loadingRequestAccessList,
    customerGroups
  } = useSelector((state) => state.customers);
  const {
    isEnabledRequestAccess
  } = useSelector((state) => state.store_configs);


  useEffect(() => {
    getStoreRequestAccessListApi({
      dispatch,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions: Object.values(filterConditions)
        .filter((condition) => condition.value !== ''),
      searchConditions
    });
  }, [
    sortDirection,
    sortField,
    rowsPerPage,
    currentPage,
    filterConditions,
    searchConditions
  ]);

  useEffect(() => {
    if (customerGroups) {
      getStoreCustomerGroupsApi({dispatch});
    }

    if (!isEnabledRequestAccess) {
      history.push('/');
    }
  }, []);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleSelectAllCustomers = (event) => {
    const {checked} = event.target;
    const allCustomersSelected = checked ? [...requestAccessCustomersList] : [];
    setCheckedCustomers(allCustomersSelected);
  };

  const handleChangeSorting = (property) => {
    const isAsc = sortField === property && sortDirection === 'desc';
    setSortDirection(isAsc ? 'asc' : 'desc');
    setSortField(property);
  };

  const handleSelectCustomerRow = (requestAccessRowData, isItemSelected) => {
    if (isItemSelected) {
      setCheckedCustomers([...checkedCustomers, requestAccessRowData]);
    } else {
      const filterOnlyCheckedCustomers = checkedCustomers.filter((item) => item.id !== requestAccessRowData.id);
      setCheckedCustomers(filterOnlyCheckedCustomers);
    }
  };

  const handleSearch = (searchValue, searchField) => {
    const condition = {
      field: searchField,
      value: searchValue.length ? `%${searchValue}%` : '',
      conditionType: 'like'
    };

    const typesOfCustomerGroups = [...defaultCustomerGroups, ...customerGroups];

    if (searchField === 'group_id') {
      const matchedCustomerGroups = typesOfCustomerGroups.filter((group) => group.code.toLowerCase().includes(searchValue.toLowerCase()));
      const matchedCustomerGroupId = JSON.stringify(matchedCustomerGroups.map((v) => v.id)).slice(1, -1);

      setSearchConditions(searchValue.length ?
        {...condition, value: matchedCustomerGroupId.length ? matchedCustomerGroupId : -1, conditionType: 'in'}
        :
        {});
      setCurrentPage(1);
      return;
    }

    setSearchConditions(searchValue.length ? condition : {});
    setCurrentPage(1);
  };


  return (
    <div className="scroll-y pb-5">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Customers'},
            { name: 'Request Access List'}
          ]}
        />
      </div>
      <Card className="mx-10 mt-10">
        <div className={`py-5 ${classes.toolbarContainer}`}>
          <RequestAccessTableToolbar
            filterConditions={filterConditions}
            onSearch={handleSearch}
            checkedCustomers={checkedCustomers}
            setOpenFilter={setOpenFilter}
          />
        </div>
        <div className="table-container">
          <TableContainer>
            <Table aria-label="table" className={classes.tableRequestAccess} >
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" >
                    <Checkbox
                      color="primary"
                      indeterminate={checkedCustomers.length > 0 && checkedCustomers.length < requestAccessCustomersList.length}
                      checked={requestAccessCustomersList?.length > 0 && checkedCustomers?.length === requestAccessCustomersList?.length}
                      onChange={handleSelectAllCustomers}
                    />
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'entity_id'}
                      IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={() => handleChangeSorting('entity_id')}
                    >
                      Customer ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'email'}
                      IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={() => handleChangeSorting('email')}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'firstname'}
                      IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={() => handleChangeSorting('firstname')}
                    >
                      First name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'lastname'}
                      IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={() => handleChangeSorting('lastname')}
                    >
                      Last name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'group_id'}
                      IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={() => handleChangeSorting('group_id')}
                    >
                      Customer Group
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'request_access'}
                      IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={() => handleChangeSorting('request_access')}
                    >
                      Request Access Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    Manage Access
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!requestAccessCustomersList?.length ?
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No matching customers found.
                    </TableCell>
                  </TableRow>
                  :
                  <>
                    {requestAccessCustomersList.map((requestAccessRowData) => {
                      const isItemSelected = checkedCustomers.filter((customerRow) => customerRow.id === requestAccessRowData.id)?.length === 1;
                      return (
                        <RequestAccessRow
                          key={requestAccessRowData.id}
                          requestAccessRowData={requestAccessRowData}
                          isItemSelected={isItemSelected}
                          handleSelectCustomerRow={handleSelectCustomerRow}
                        />
                      );
                    })
                    }
                  </>
                }
              </TableBody>
            </Table>
          </TableContainer>
          {!!customersTotalCount &&
            <TablePagination
              rowsPerPageOptions={[10, 20, 40, 80, 100]}
              component="div"
              className={classes.paginationContainer}
              count={customersTotalCount}
              rowsPerPage={rowsPerPage}
              page={currentPage - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          }
        </div>
      </Card>
      <Drawer
        anchor="right"
        open={openFilter}
        onClose={() => setOpenFilter((openFilter) => !openFilter)}
        PaperProps={{ style: { maxWidth: '400px' } }}
      >
        <FilterDrawer
          setOpenFilter={setOpenFilter}
          setFilterConditions={setFilterConditions}
          filterConditions={filterConditions}
        />
      </Drawer>
      <BackdropCircular loading={loadingRequestAccessList} />
    </div>
  );
};

export default RequestAccessList;
